import React, { useState } from 'react'
import { MenuItem, Icon, CircularProgress, Select } from '@material-ui/core'
import { useDispatch } from 'react-redux'
import MessageAlert from 'app/components/MessageAlert'
import CancelButton from 'app/components/buttons/CancelButton'
import DangerousButton from 'app/components/DangerousButton'
import effects from 'app/effects/effects'
import clsx from 'clsx'
import { useIntl, FormattedMessage } from 'react-intl'
import ConfirmationDialog from 'app/components/ConfirmationDialog'
import FetchTable from 'app/components/table/FetchTable'
import FilterListIcon from '@material-ui/icons/FilterList'
import { withBeaconImageColumn } from 'app/components/table/TableColumn'
import useTableDelete from 'commons/useTableDelete'
import DefaultSwitch from 'app/components/switch/DefaultSwitch'
import BeaconFormDialog from './BeaconFormDialog'

import * as R from 'ramda'
import { BatteryIcon } from './BeaconsTableUtils'
import { makeStyles } from '@material-ui/styles'
import { FILTER_TYPE } from './ManageBeacons'

const useStyles = makeStyles(() => ({
  statusSelect: {
    paddingTop: 1,
    fontSize: 12,
    color: '#404040',
    '&::before': {
      display: 'none',
    },
    '&::after': {
      display: 'none',
    },
    '& .MuiSelect-select': {
      color: '#0073b1',
      padding: '9px 32px 9px 16px',
      backgroundColor: '#e5f1f7',
      borderRadius: 20,
    },
  },
  selectIcon: {
    position: 'absolute',
    right: 10,
    pointerEvents: 'none',
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0073b1',
  },
}))
const BeaconsTable = ({
  pageInfo,
  onFetchData,
  refetchData,
  showErrorDialogue,
  startFetchData,
  endFetchData,
  showPagination,
  onChangeFilterByKey,
  defaultPageSize,
  filter,
}) => {
  const classes = useStyles()
  const { formatMessage } = useIntl()
  const [editingData, setEditingData] = useState({})
  const [isOpenEditBeaconFormDialog, setIsOpenEditBeaconFormDialog] =
    useState(false)

  const handleClickEditData = (value) => {
    setIsOpenEditBeaconFormDialog(true)
    setEditingData({ ...value, isActive: Boolean(value.isActive) })
  }

  const onDeleteSuccessPromise = () => {
    startFetchData()
    return refetchData().then(endFetchData)
  }

  const {
    afterConfirmPending,
    onConfirmDelete,
    isOpenDeleteConfirmation,
    onCancelDelete,
  } = useTableDelete({
    asyncDelete: effects.apis.deleteBeacons,
    showErrorDialogue,
    onDeleteSuccessPromise,
  })

  return (
    <div className="h-full overflow-hidden flex flex-col">
      {!pageInfo.error && (
        <div className="flex items-center mb-16">
          <FilterListIcon className="mr-8" />
          <Select
            className={clsx(classes.statusSelect)}
            value={filter['status']}
            onChange={onChangeFilterByKey('status')}
            IconComponent={() => (
              <Icon className={classes.selectIcon}>
                keyboard_arrow_down_icon
              </Icon>
            )}
          >
            <MenuItem value={FILTER_TYPE.ACTIVE_AND_INACTIVE}>
              <FormattedMessage id="beacons.table.filter.allStatuses" />
            </MenuItem>
            <MenuItem value={FILTER_TYPE.ACTIVE}>
              <FormattedMessage id="beacons.table.filter.active" />
            </MenuItem>
            <MenuItem value={FILTER_TYPE.INACTIVE}>
              <FormattedMessage id="beacons.table.filter.inactive" />
            </MenuItem>
          </Select>
        </div>
      )}
      <FetchTable
        onClickRow={handleClickEditData}
        loading={pageInfo.isLoading}
        FilterComponent={null}
        data={pageInfo.data}
        total={pageInfo.totalData}
        pages={pageInfo.total}
        error={pageInfo.error}
        refetchData={refetchData}
        manual
        defaultPageSize={defaultPageSize}
        onFetchData={onFetchData}
        noDataText={formatMessage({ id: 'beaconList.table.empty.label' })}
        showPagination={showPagination}
        columns={[
          withBeaconImageColumn(),
          {
            Header: formatMessage({ id: 'beacons.table.name' }),
            accessor: 'name',
            filterable: true,
            type: 'text',
          },
          {
            Header: formatMessage({ id: 'beacons.table.location' }),
            accessor: 'location',
            sortable: false,
            filterable: true,
            type: 'text',
          },
          {
            Header: formatMessage({ id: 'beacons.table.beaconId' }),
            accessor: 'physicalAddress',
            sortable: false,
            filterable: true,
            type: 'text',
          },
          {
            Header: formatMessage({ id: 'beacons.table.notifications' }),
            accessor: 'notifications',
            sortable: false,
            filterable: true,
            type: 'text',
            Cell: (props) =>
              R.length(props.original.alerts || []).toLocaleString(),
          },
          {
            Header: formatMessage({ id: 'beacons.table.battery' }),
            accessor: 'batteryPercentage',
            filterable: true,
            type: 'text',
            Cell: BatteryIcon,
          },
          {
            Header: formatMessage({ id: 'beacons.table.onOff' }),
            accessor: 'isActive',
            filterable: false,
            sortable: false,
            width: 128,
            Cell: (props) => (
              <AsyncSwitch
                {...props}
                onChange={async () => {
                  await effects.apis.updateBeacons({
                    ...props.original,
                    physicalId: props.original.physicalAddress,
                    isActive: Number(!props.original.isActive),
                  })
                  await refetchData(false)
                }}
              />
            ),
          },
          // withDeleteColumn(handlerDeleteOne),
        ]}
      />
      <BeaconFormDialog
        initialValues={editingData}
        isOpen={isOpenEditBeaconFormDialog}
        refetchData={refetchData}
        onClose={() => {
          setIsOpenEditBeaconFormDialog(false)
        }}
      />
      <ConfirmationDialog
        open={isOpenDeleteConfirmation}
        title={formatMessage({ id: 'beacons.delete.title' })}
        body={formatMessage({ id: 'beacons.delete.description' })}
        CancelButton={
          <CancelButton
            onClick={() => {
              onCancelDelete()
            }}
          />
        }
        ConfirmButton={
          <DangerousButton
            onClick={async () => {
              onConfirmDelete()
            }}
            isLoading={afterConfirmPending}
          >
            <FormattedMessage id="action.delete" />
          </DangerousButton>
        }
      />
    </div>
  )
}

export default BeaconsTable

const AsyncSwitch = (props) => {
  const { onChange } = props
  const { formatMessage } = useIntl()
  const reduxDispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)
  return (
    <>
      <DefaultSwitch
        checked={Boolean(props.original.isActive)}
        onClick={(event) => {
          event.stopPropagation()
        }}
        onChange={async () => {
          setIsLoading(true)
          try {
            await onChange()
          } catch (error) {
            reduxDispatch(MessageAlert.showNetworkError(formatMessage)(error))
          } finally {
            setIsLoading(false)
          }
        }}
      />
      {isLoading && (
        <CircularProgress size={24} className="ltr:ml-8 rtl:mr-8" />
      )}
    </>
  )
}
